/**
 * 登录页面
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, GalleryVerticalEnd } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import { z } from "zod";

import {
  authApi,
  buildLoginPath,
  getAuthReturnPath,
  resolvePostLoginPath,
  useLoginMutation,
  useQQLoginMutation,
  useWechatLoginMutation,
} from "@/modules/auth";
import { isOnboardingPending } from "@/modules/onboarding/state";
import qqIcon from "@/shared/assets/icons/qq.svg";
import wechatIcon from "@/shared/assets/icons/wechat.svg";
import { ROUTE_PATHS } from "@/shared/constants/route-paths";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/shared/ui/field";
import { Input } from "@/shared/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/shared/ui/input-group";
import { Separator } from "@/shared/ui/separator";
import { Spinner } from "@/shared/ui/spinner";

const phoneRegExp = /^1[3-9]\d{9}$/;
const AUTH_PROVIDER_PARAM = "auth_provider";
const WECHAT_AUTH_PROVIDER = "wechat";
const QQ_AUTH_PROVIDER = "qq";

function isPhoneAccount(account: string) {
  return phoneRegExp.test(account);
}

const LoginPage = () => {
  const { t } = useTranslation("login");
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [isWechatRedirecting, setIsWechatRedirecting] = useState(false);
  const [isQQRedirecting, setIsQQRedirecting] = useState(false);
  const loginMutation = useLoginMutation();
  const qqLoginMutation = useQQLoginMutation();
  const wechatLoginMutation = useWechatLoginMutation();
  const returnPath = getAuthReturnPath(location.search);

  const formSchema = z.object({
    email: z.string().refine((value) => z.string().email().safeParse(value).success || isPhoneAccount(value), {
      message: t("validation.account"),
    }),
    password: z.string().min(6, {
      message: t("validation.password"),
    }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const createAuthRedirectUrl = useCallback((provider: string) => {
    const loginPath = returnPath
      ? buildLoginPath(returnPath)
      : ROUTE_PATHS.LOGIN;
    const redirectUrl = new URL(loginPath, window.location.origin);
    redirectUrl.searchParams.set(AUTH_PROVIDER_PARAM, provider);
    return redirectUrl.toString();
  }, [returnPath]);

  const handleWechatLogin = async () => {
    try {
      setIsWechatRedirecting(true);
      const response = await authApi.getWechatQRCodeLink({
        next: createAuthRedirectUrl(WECHAT_AUTH_PROVIDER),
      });
      window.location.href = response.url;
    } catch {
      setIsWechatRedirecting(false);
    }
  };

  const handleQQLogin = async () => {
    try {
      setIsQQRedirecting(true);
      const response = await authApi.getQQConnectLink({
        next: createAuthRedirectUrl(QQ_AUTH_PROVIDER),
      });
      window.location.href = response.url;
    } catch {
      setIsQQRedirecting(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const authProvider = params.get(AUTH_PROVIDER_PARAM);
    const code = params.get("code");
    const state = params.get("state");

    if (!code || !state) {
      return;
    }

    const authMutation =
      authProvider === WECHAT_AUTH_PROVIDER
        ? wechatLoginMutation
        : authProvider === QQ_AUTH_PROVIDER
          ? qqLoginMutation
          : null;

    if (!authMutation || authMutation.isPending) {
      return;
    }

    authMutation.mutate(
      authProvider === QQ_AUTH_PROVIDER
        ? { code, state, next: createAuthRedirectUrl(QQ_AUTH_PROVIDER) }
        : { code, state },
      {
        onSuccess: (response) => {
          toast.success(t("loginSuccess"));
          navigate(
            resolvePostLoginPath({
              onboardingPending: isOnboardingPending(response.token),
              returnPath,
            }),
            { replace: true },
          );
        },
        onError: () => {
          navigate(
            returnPath ? buildLoginPath(returnPath) : ROUTE_PATHS.LOGIN,
            { replace: true },
          );
        },
      },
    );
  }, [
    createAuthRedirectUrl,
    location.search,
    navigate,
    qqLoginMutation,
    returnPath,
    t,
    wechatLoginMutation,
  ]);

  function onSubmit(values: z.infer<typeof formSchema>) {
    const account = values.email.trim();
    loginMutation.mutate(
      isPhoneAccount(account)
        ? {
            phone: account,
            password: values.password,
          }
        : {
            email: account,
            password: values.password,
          },
      {
        onSuccess: (response) => {
          form.reset();
          toast.success(t("loginSuccess"));
          navigate(resolvePostLoginPath({
            onboardingPending: isOnboardingPending(response.token),
            returnPath,
          }), { replace: true });
        },
      }
    );
  }

  return (
    <div className="auth-form flex w-full max-w-md flex-col gap-6">
      <a href={ROUTE_PATHS.HOME} className="flex items-center gap-2 self-center font-medium">
        <div className="bg-primary text-primary-foreground flex size-6 items-center justify-center rounded-md">
          <GalleryVerticalEnd className="size-4" />
        </div>
        Pinbox
      </a>
      <div className={cn("flex flex-col gap-6")}>
        <Card>
          <CardHeader className="px-8 text-center">
            <CardTitle className="text-xl">{t("title")}</CardTitle>
            <CardDescription>{t("description")}</CardDescription>
          </CardHeader>
          <CardContent className="px-8">
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <FieldGroup>
                <Controller
                  name="email"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor="email">
                        {t("accountLabel")}
                      </FieldLabel>
                      <Input
                        {...field}
                        className="h-11"
                        aria-invalid={fieldState.invalid}
                        id="email"
                        type="text"
                        placeholder={t("accountPlaceholder")}
                        autoComplete="username"
                      />
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  )}
                />

                <Controller
                  name="password"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <div className="flex items-center">
                        <FieldLabel htmlFor="password">
                          {t("passwordLabel")}
                        </FieldLabel>
                        <Link
                          to={ROUTE_PATHS.SEND_RESET_LINK}
                          className="ml-auto text-sm underline-offset-4 hover:underline"
                        >
                          {t("forgotPassword")}
                        </Link>
                      </div>
                      <InputGroup className="h-11">
                        <InputGroupInput
                          {...field}
                          className="h-full"
                          aria-invalid={fieldState.invalid}
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder={t("passwordPlaceholder")}
                          autoComplete="current-password"
                        />
                        <InputGroupAddon align="inline-end">
                          <InputGroupButton
                            aria-label="Eye"
                            title="toggle password visibility"
                            size="icon-xs"
                            onClick={togglePasswordVisibility}
                            type="button"
                          >
                            {showPassword ? (
                              <Eye className="h-4 w-4" />
                            ) : (
                              <EyeOff className="h-4 w-4" />
                            )}
                          </InputGroupButton>
                        </InputGroupAddon>
                      </InputGroup>
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  )}
                />
                <Field>
                  <Button
                    type="submit"
                    className="h-11"
                    disabled={loginMutation.isPending}
                  >
                    {loginMutation.isPending && <Spinner />}
                    {t("submitButton")}
                  </Button>
                  <FieldDescription className="text-center">
                    {t("noAccount")}{" "}
                    <Link className="font-medium" to={ROUTE_PATHS.SIGNUP}>
                      {" "}
                      {t("signup")}
                    </Link>
                  </FieldDescription>
                </Field>
              </FieldGroup>
            </form>

            <div className="auth-login-providers mb-5 mt-10 flex items-center gap-3">
              <Separator className="flex-1" />
              <span className="text-muted-foreground text-xs">{t("orLoginWith")}</span>
              <Separator className="flex-1" />
            </div>

            <div className="flex justify-center gap-5">
              <button
                type="button"
                className="flex size-9 items-center justify-center rounded-full bg-[#07C160]/10 transition-colors hover:bg-[#07C160]/15"
                aria-label={t("wechatLogin")}
                disabled={isWechatRedirecting || wechatLoginMutation.isPending}
                onClick={handleWechatLogin}
              >
                {isWechatRedirecting || wechatLoginMutation.isPending ? (
                  <Spinner className="size-4" />
                ) : (
                  <img src={wechatIcon} alt="" className="size-5" />
                )}
              </button>
              <button
                type="button"
                className="flex size-9 items-center justify-center rounded-full bg-[#12B7F5]/10 transition-colors hover:bg-[#12B7F5]/15"
                aria-label={t("qqLogin")}
                disabled={isQQRedirecting || qqLoginMutation.isPending}
                onClick={handleQQLogin}
              >
                {isQQRedirecting || qqLoginMutation.isPending ? (
                  <Spinner className="size-4" />
                ) : (
                  <img src={qqIcon} alt="" className="size-5" />
                )}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
